import type { WindowState } from '@/wm/types';

/**
 * "Group similar taskbar buttons".
 *
 * XP did not group on principle — it grouped when it ran out of room, and it
 * collapsed the biggest program first, one at a time, until the buttons fit.
 * That is what this reproduces: with three windows open nothing is grouped no
 * matter how many share a program, and with fifteen the crowd is folded away
 * largest-first until the rest are readable again.
 *
 * Pure, so the arithmetic can be tested without a taskbar.
 */

export type TaskItem =
  | { kind: 'window'; win: WindowState }
  | { kind: 'group'; appId: string; windows: WindowState[] };

/**
 * Below this a button is an icon and an ellipsis. It is the number that decides
 * how many buttons the bar will show before it starts grouping.
 */
export const MIN_READABLE_TASK_W = 108;

/** How many buttons a strip of this width can show without grouping. */
export function taskCapacity(width: number): number {
  return Math.max(1, Math.floor(width / MIN_READABLE_TASK_W));
}

/** Windows per program, in the order the programs first appear. */
function groupsOf(tasks: readonly WindowState[]): Map<string, WindowState[]> {
  const groups = new Map<string, WindowState[]>();
  for (const win of tasks) {
    const existing = groups.get(win.appId);
    if (existing) existing.push(win);
    else groups.set(win.appId, [win]);
  }
  return groups;
}

export function groupTasks(tasks: readonly WindowState[], capacity: number): TaskItem[] {
  const room = Math.max(1, capacity);
  if (tasks.length <= room) return tasks.map((win) => ({ kind: 'window', win }));

  const groups = groupsOf(tasks);
  const collapsed = new Set<string>();
  let buttons = tasks.length;

  // Collapse the largest group, then look again. Ties go to whichever program
  // opened first, which keeps the choice stable as windows come and go.
  while (buttons > room) {
    let biggest: string | null = null;
    let size = 1;
    for (const [appId, windows] of groups) {
      if (collapsed.has(appId) || windows.length <= size) continue;
      biggest = appId;
      size = windows.length;
    }
    // Nothing left worth folding: every remaining program has one window, and
    // a single window is a button, not a group.
    if (biggest === null) break;

    collapsed.add(biggest);
    buttons -= size - 1;
  }

  const emitted = new Set<string>();
  const items: TaskItem[] = [];
  for (const win of tasks) {
    if (!collapsed.has(win.appId)) {
      items.push({ kind: 'window', win });
      continue;
    }
    // The group takes the slot its first window would have had.
    if (emitted.has(win.appId)) continue;
    emitted.add(win.appId);
    items.push({ kind: 'group', appId: win.appId, windows: groups.get(win.appId) ?? [win] });
  }
  return items;
}
